import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CreditCard, Shield, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { usStates } from "@/utils/states";
import { trackSubscribe } from "@/lib/facebookPixel";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

interface PaymentMethodFormProps {
  selectedPlan: 'monthly' | 'annual';
  onSuccess: () => void;
  onBack: () => void;
}

const PaymentMethodForm = ({ selectedPlan, onSuccess, onBack }: PaymentMethodFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US'
    }
  });
  const [errors, setErrors] = useState({
    name: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    address: ''
  });

  const stripeElementStyle = {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'inherit',
      '::placeholder': {
        color: 'rgba(255, 255, 255, 0.4)',
      },
      iconColor: '#ffd700',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
    complete: {
      iconColor: '#10b981',
    },
  };

  useEffect(() => {
    const setupPaymentMethod = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('setup-payment-method', {
          body: { plan_type: selectedPlan }
        });

        if (error) throw error;
        
        setClientSecret(data.client_secret);
      } catch (err: any) {
        setError(err.message || "Failed to initialize payment setup");
      }
    };

    setupPaymentMethod();
  }, [selectedPlan]);

  const validateForm = (): boolean => {
    const newErrors = {
      name: '',
      cardNumber: '',
      cardExpiry: '',
      cardCvc: '',
      address: ''
    };

    if (!billingDetails.name.trim()) {
      newErrors.name = 'Cardholder name is required';
    }

    if (!billingDetails.address.line1.trim()) {
      newErrors.address = 'Street address is required';
    }
    if (!billingDetails.address.city.trim()) {
      newErrors.address = 'City is required';
    }
    if (!billingDetails.address.state) {
      newErrors.address = 'State is required';
    }
    if (!billingDetails.address.postal_code.trim()) {
      newErrors.address = 'ZIP code is required';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);
    setError("");

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setError("Card element not found");
      setIsProcessing(false);
      return;
    }

    try {
      const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              name: billingDetails.name,
              address: billingDetails.address
            }
          }
        }
      );

      if (setupError) {
        throw new Error(setupError.message);
      }

      if (setupIntent.status === 'succeeded') {
        // Create subscription immediately after payment setup succeeds
        const { error: subError } = await supabase.functions.invoke('create-subscription-from-trial');
        
        if (subError) {
          console.error('Subscription creation error:', subError);
          toast({
            title: "Warning",
            description: "Payment method saved, but subscription setup needs attention. Please contact support.",
            variant: "destructive"
          });
        } else {
          // Track Subscribe event for Facebook Pixel (only if subscription created successfully)
          const subscriptionValue = selectedPlan === 'monthly' ? 34.99 : 349.99;
          trackSubscribe(subscriptionValue, selectedPlan, 'seller');
        }
        
        toast({
          title: "Success",
          description: "Payment method saved successfully!",
        });
        onSuccess();
      } else {
        throw new Error("Failed to confirm payment method setup");
      }
    } catch (err: any) {
      setError(err.message || "Failed to setup payment method");
    } finally {
      setIsProcessing(false);
    }
  };

  const planDetails = {
    monthly: { name: 'Monthly Shark', price: '$34.99/month (was $68)' },
    annual: { name: 'Annual Shark', price: '$349.99/year (was $680)', savings: 'Save $69.89 vs. monthly' }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Setup Payment Method</h2>
        <p className="text-muted-foreground">
          Add your payment method to start your 30-day free trial
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="w-5 h-5" />
            <span>Selected Plan: {planDetails[selectedPlan].name}</span>
          </CardTitle>
          <div className="text-sm text-muted-foreground">
            Price: {planDetails[selectedPlan].price}
            {selectedPlan === 'annual' && (
              <span className="text-green-600 ml-2">({planDetails[selectedPlan].savings})</span>
            )}
          </div>
        </CardHeader>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Payment Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Cardholder Name */}
            <div className="space-y-2">
              <Label htmlFor="cardholderName">Cardholder Name</Label>
              <Input
                id="cardholderName"
                placeholder="John Doe"
                value={billingDetails.name}
                onChange={(e) => setBillingDetails({...billingDetails, name: e.target.value})}
                className="bg-card border-border"
              />
              {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
            </div>

            {/* Card Number */}
            <div className="space-y-2">
              <Label>Card Number</Label>
              <div className="p-3 border border-border rounded-md bg-card">
                <CardNumberElement options={{ style: stripeElementStyle }} />
              </div>
            </div>

            {/* Expiry and CVC in Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expiration Date</Label>
                <div className="p-3 border border-border rounded-md bg-card">
                  <CardExpiryElement options={{ style: stripeElementStyle }} />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>CVC</Label>
                <div className="p-3 border border-border rounded-md bg-card">
                  <CardCvcElement options={{ style: stripeElementStyle }} />
                </div>
              </div>
            </div>

            {/* Billing Address Section */}
            <div className="pt-4 border-t border-border space-y-4">
              <h3 className="text-sm font-semibold">Billing Address</h3>
              
              {/* Address Line 1 */}
              <div className="space-y-2">
                <Label htmlFor="addressLine1">Street Address</Label>
                <Input
                  id="addressLine1"
                  placeholder="123 Main St"
                  value={billingDetails.address.line1}
                  onChange={(e) => setBillingDetails({
                    ...billingDetails,
                    address: {...billingDetails.address, line1: e.target.value}
                  })}
                  className="bg-card border-border"
                />
              </div>

              {/* Address Line 2 (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="addressLine2">Apartment, suite, etc. (optional)</Label>
                <Input
                  id="addressLine2"
                  placeholder="Apt 4B"
                  value={billingDetails.address.line2}
                  onChange={(e) => setBillingDetails({
                    ...billingDetails,
                    address: {...billingDetails.address, line2: e.target.value}
                  })}
                  className="bg-card border-border"
                />
              </div>

              {/* City, State, ZIP in Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    placeholder="New York"
                    value={billingDetails.address.city}
                    onChange={(e) => setBillingDetails({
                      ...billingDetails,
                      address: {...billingDetails.address, city: e.target.value}
                    })}
                    className="bg-card border-border"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select
                    value={billingDetails.address.state}
                    onValueChange={(value) => setBillingDetails({
                      ...billingDetails,
                      address: {...billingDetails.address, state: value}
                    })}
                  >
                    <SelectTrigger className="bg-card border-border">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {usStates.map(state => (
                        <SelectItem key={state.id} value={state.id}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postalCode">ZIP Code</Label>
                  <Input
                    id="postalCode"
                    placeholder="10001"
                    value={billingDetails.address.postal_code}
                    onChange={(e) => setBillingDetails({
                      ...billingDetails,
                      address: {...billingDetails.address, postal_code: e.target.value}
                    })}
                    className="bg-card border-border"
                    maxLength={10}
                  />
                </div>
              </div>

              {errors.address && <p className="text-sm text-destructive">{errors.address}</p>}
            </div>
          </CardContent>
        </Card>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900">Secure & Protected</p>
              <p className="text-sm text-blue-700">
                Your payment information is encrypted and secure. You won't be charged until your 30-day free trial ends.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <Button
            type="submit"
            className="w-full"
            disabled={!stripe || !clientSecret || isProcessing}
          >
            {isProcessing ? "Setting up..." : "Start Free Trial"}
          </Button>
          
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onBack}
            disabled={isProcessing}
          >
            Back to Plan Selection
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          By continuing, you agree to our terms of service and privacy policy.
          Your trial will begin immediately and billing will start after 30 days.
        </p>
      </form>
    </div>
  );
};

interface PaymentMethodStepProps {
  selectedPlan: 'monthly' | 'annual';
  onSuccess: () => void;
  onBack: () => void;
}

const PaymentMethodStep = (props: PaymentMethodStepProps) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentMethodForm {...props} />
    </Elements>
  );
};

export default PaymentMethodStep;